/**
 * Attunement — Tine's signature (BULLET-HELL.md §D7, the champion promotion).
 *
 * §D7 built the polarity *payoff*: absorbing a matched-tone bullet pays
 * ultimate charge and banks toward a homing release (Resonance +
 * polarityRelease.js). That payoff could only ever fire in Act II, because
 * Act II's Antiphon is the only thing in the game that tones bullets at all —
 * an income stream one boss-set wide. The champion promotion is the rest of
 * the idea: a champion who tones them *herself*, so polarity is how every
 * boss in the game is fought rather than a gimmick you meet once.
 *
 * This module is the stamp. `createToneCaster(config)` returns a function
 * BulletPool.fire() calls on every freshly spawned bullet (see
 * `BulletPool.toneCaster`), which tones it and recolours it to match.
 *
 * Decisions the shape of this encodes:
 *
 * - **Alternating, not random.** Consecutive bullets take opposite tones, so
 *   any ring, wall or fan comes out as a checkerboard rather than as a slab
 *   the player might have no attunement answer to. Randomness would
 *   occasionally hand a pattern one tone end to end, which in a game whose
 *   whole fairness contract is "the telegraph told you" is not a difficulty
 *   spike, it's a lie. Alternation also guarantees the mechanic's promise:
 *   roughly half of everything on screen is always yours to swim through.
 * - **Only untoned bullets.** The Antiphon's own toned fire keeps the tones
 *   its patterns authored (TwinVoice's interleaved halves are a designed
 *   read, and overwriting them would replace a card with noise).
 * - **The bullet is recoloured.** Tone has to be readable from across the
 *   arena, which means it owns the bullet's colour — the same two hues the
 *   Antiphon uses (`warmColor`/`coldColor`), so a player who learned polarity
 *   there reads Tine's screen with no relearning. It costs pattern-identity
 *   colour, which is the trade the champion is: her runs look like the
 *   Antiphon's fight, everywhere.
 * - **Mismatch bites.** The other half of the screen hits for
 *   `mismatchBonus` extra, exactly as in Act II. Without it the signature is
 *   "half of all incoming fire is deleted" and no attunement decision has a
 *   wrong answer.
 */
export function createToneCaster({ warmColor, coldColor, mismatchBonus = 0 }) {
  // Module-free state: one caster per pool, so two spawning systems (a herald
  // pair) alternate independently and neither can desync the other's rings.
  let warm = false;
  return (bullet) => {
    // Already toned (Act II's own polarity fire) — leave the card's read alone.
    if (!bullet || bullet.tone) return bullet;
    warm = !warm;
    bullet.tone = warm ? 'warm' : 'cold';
    bullet.mismatchBonus = mismatchBonus;
    bullet.color = warm ? warmColor : coldColor;
    return bullet;
  };
}
